import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shuffle, Play } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const ROLE_OPTS = [["sales", "Sales reps"], ["broker", "Brokers"]];

export const MessageRoutingCard = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/contact/routing").then(({ data }) => setCfg(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async (patch) => {
    setSaving(true);
    try { const { data } = await api.put("/contact/routing", { ...cfg, ...patch }); setCfg(data); toast.success("Routing updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const run = async () => {
    try { const { data } = await api.post("/contact/routing/run"); toast.success(`Assigned ${data.assigned} of ${data.unowned} unowned messages`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not run"); }
  };
  if (!cfg) return null;
  const toggleRole = (r) => { const roles = cfg.roles.includes(r) ? cfg.roles.filter((x) => x !== r) : [...cfg.roles, r]; if (roles.length) save({ roles }); };
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="message-routing-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900 dark:text-slate-100"><Shuffle className="h-4 w-4 text-rose-600" /> Auto-assign messages</h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Round-robin new contact messages to available reps so nothing sits unowned.</p>
        </div>
        <Switch checked={cfg.enabled} disabled={saving} onCheckedChange={(v) => save({ enabled: v })} data-testid="routing-enabled-switch" />
      </div>
      <div className="mt-4">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">Route to</p>
        <div className="mt-1.5 flex gap-2">
          {ROLE_OPTS.map(([r, l]) => (
            <button key={r} onClick={() => toggleRole(r)} data-testid={`routing-role-${r}`}
              className={`rounded-full border px-3 py-1 text-xs font-bold transition-colors ${cfg.roles.includes(r) ? "border-rose-600 bg-rose-600 text-white" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-4">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">In rotation ({cfg.eligible.length})</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="routing-eligible">
          {cfg.eligible.length === 0 && <span className="text-xs text-slate-400">No active members with these roles.</span>}
          {cfg.eligible.map((e) => (
            <span key={e.user_id} className={`rounded-md px-2 py-0.5 text-xs font-semibold ${e.user_id === cfg.last_assignee_id ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`}>
              {e.name}{e.user_id === cfg.last_assignee_id ? " · last" : ""}
            </span>
          ))}
        </div>
      </div>
      <Button size="sm" variant="outline" disabled={!cfg.enabled} onClick={run} data-testid="routing-run-now" className="mt-4 w-full rounded-md text-xs font-bold"><Play className="mr-1 h-3.5 w-3.5" /> Assign unowned messages now</Button>
    </div>
  );
};
